'use client';

/**
 * Generic login (employee/client audience) — ui-ux-pro-max
 * Follows app ThemeSystem light (cream + rose gold) / dark (espresso).
 */
import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { Form, Input, Button } from 'antd';
import {
  UserOutlined,
  LockOutlined,
  SafetyOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  KeyOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { useTheme, ThemeToggle } from '@/shared/theme';
import webauthnService from '@/domains/auth/services/webauthn-service';
import { useAppDispatch, useAppSelector } from '@/shared/providers/store';
import { loginAsync, clearErrors, selectAuth, initializeAuth } from '@/shared/providers/state/authSlice';
import '@/shared/theme/styles/login.css';
import styles from './AuthLayout.module.css';

type LoginAudience = 'employee' | 'client';
type AuthMode = 'password' | 'mfa' | 'passkey';

interface LoginPageProps {
  audience?: LoginAudience;
}

const MODE_HINT: Record<AuthMode, string> = {
  password: 'Sign in with username and password',
  mfa: 'Sign in with username and MFA code only',
  passkey: 'Sign in with biometric authentication or a security key',
};

const LoginPage: React.FC<LoginPageProps> = ({ audience = 'employee' }) => {
  const [form] = Form.useForm();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { isLoading, loginError, requiresMfa, mfaError, isAuthenticated } = useAppSelector(selectAuth);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>('password');
  const theme = useTheme();
  const errorRef = useRef<HTMLDivElement>(null);

  const loginEndpoint = audience === 'client' ? '/api/auth/login' : '/api/auth/employee-login';
  const redirectTo = audience === 'client' ? '/client/dashboard' : '/dashboard';
  const mfaRedirectTo = '/auth/mfa/provisional-setup';

  const persistClientSession = (payload?: {
    sessionToken?: string;
    session_token?: string;
    access_token?: string;
    accessToken?: string;
  }) => {
    if (audience !== 'client' || typeof window === 'undefined') return;
    const token =
      payload?.sessionToken ||
      payload?.session_token ||
      payload?.access_token ||
      payload?.accessToken;
    if (token) {
      localStorage.setItem('sessionToken', token);
    }
  };

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    dispatch(initializeAuth());
    // Always clear sign-out flag on login — SignOutScreen must not persist
    try {
      sessionStorage.removeItem('mechagora:signing_out');
    } catch {
      /* no-op */
    }
    return () => window.removeEventListener('resize', checkMobile);
  }, [dispatch]);

  useEffect(() => {
    // Do not auto-bounce into the app while a *fresh* sign-out is settling
    let signingOut = false;
    try {
      const raw = sessionStorage.getItem('mechagora:signing_out');
      if (raw && raw !== '1') {
        const age = Date.now() - Number(raw);
        signingOut = Number.isFinite(age) && age >= 0 && age < 8000;
      }
    } catch {
      /* no-op */
    }
    if (isAuthenticated && !signingOut) {
      router.replace(redirectTo);
    }
  }, [isAuthenticated, redirectTo, router]);

  useEffect(() => {
    if (requiresMfa) {
      router.push(mfaRedirectTo);
    }
  }, [requiresMfa, router, mfaRedirectTo]);

  useEffect(() => {
    if (loginError) {
      setError(loginError);
      dispatch(clearErrors());
    }
    if (mfaError) {
      setError(mfaError);
      dispatch(clearErrors());
    }
  }, [loginError, mfaError, dispatch]);

  useEffect(() => {
    if (!error) return;
    const t = window.setTimeout(() => errorRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [error]);

  const handlePasskeyAuth = async (username: string) => {
    try {
      if (!window.navigator.credentials || !window.PublicKeyCredential) {
        throw new Error('Passkeys are not supported in this browser');
      }
      const isPasskeySupported =
        await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      if (!isPasskeySupported) {
        throw new Error('Passkeys are not supported on this device');
      }

      const authOptions = await webauthnService.startAuthentication(username);
      const authResult = await webauthnService.completeAuthentication(authOptions);

      if (authResult.success) {
        try {
          const response = await fetch(loginEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: username,
              passkey_credential: { user_id: authResult.user_id },
              auth_mode: 'passkey',
              audience,
            }),
          });
          const loginResult = await response.json();
          if (loginResult.success) {
            persistClientSession(loginResult);
            router.push(redirectTo);
          } else {
            setError(loginResult.error || 'Passkey authentication failed');
          }
        } catch (err) {
          console.error('Passkey login API call failed:', err);
          setError('Passkey authentication failed. Please try again.');
        }
      } else {
        setError('Passkey authentication failed. Please try again.');
      }
    } catch (err) {
      console.error('Passkey authentication error:', err);
      setError(err instanceof Error ? err.message : 'Passkey authentication failed');
    }
  };

  const onFinish = async (values: { username: string; password?: string; mfa_token?: string }) => {
    try {
      setError(null);

      if (authMode === 'passkey') {
        await handlePasskeyAuth(values.username);
        return;
      }

      // LoginRequest requires password; MFA path still sends a string (empty only if form omitted)
      const loginRequest = {
        email: values.username,
        password: values.password || '',
        audience,
        ...(authMode === 'mfa' && values.mfa_token
          ? { mfa_token: values.mfa_token, auth_mode: 'mfa' as const }
          : { auth_mode: 'password' as const }),
      };

      const loginResult = await dispatch(loginAsync(loginRequest)).unwrap();
      if (loginResult?.success) {
        persistClientSession(loginResult);
      }
    } catch (err: unknown) {
      const error = err as { status?: number; message?: string };
      if (error?.status === 401) {
        setError('Invalid username or password. Please check your credentials and try again.');
      } else if (error?.status === 500) {
        setError('Server error. Please try again later or contact support if the issue persists.');
      } else if (
        error?.message?.includes('Failed to fetch') ||
        error?.message?.includes('Network')
      ) {
        setError('Network error. Please check your internet connection and try again.');
      } else {
        setError(error?.message || 'Login failed. Please try again.');
      }
    }
  };

  const subtitle =
    audience === 'client'
      ? 'Access your trading workspace'
      : 'Access your enterprise trading platform';

  return (
    <div
      className={`login-page ${theme.isDark ? 'dark' : 'light'} ${styles.page}`}
      data-theme={theme.isDark ? 'dark' : 'light'}
    >
      <nav className="login-navbar" aria-label="Primary">
        <div className="login-navbar-container">
          <a href="/" className="login-navbar-brand">
            <div className="login-logo-icon" aria-hidden>
              <span className="login-logo-text">M</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span className="login-brand-title">Mechagora</span>
              <span className="login-brand-subtitle">
                {audience === 'client' ? 'Trading Platform' : 'Enterprise Trading'}
              </span>
            </div>
          </a>
          <ThemeToggle size={isMobile ? 'small' : 'medium'} />
        </div>
      </nav>

      <div className={styles.body}>
        <div className={styles.formContainer}>
          <div className={styles.card}>
            <header className={styles.formHeader}>
              <h1 className={styles.formTitle}>Welcome back</h1>
              <p className={styles.formSubtitle}>{subtitle}</p>
            </header>

            <div className={styles.modeToggle} role="tablist" aria-label="Authentication method">
              {(
                [
                  { id: 'password', label: 'Password', icon: <LockOutlined aria-hidden /> },
                  { id: 'mfa', label: 'MFA', icon: <SafetyOutlined aria-hidden /> },
                  { id: 'passkey', label: 'Passkey', icon: <KeyOutlined aria-hidden /> },
                ] as const
              ).map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  role="tab"
                  aria-selected={authMode === mode.id}
                  onClick={() => setAuthMode(mode.id)}
                  className={`${styles.modeBtn} ${authMode === mode.id ? styles.modeBtnActive : ''}`}
                >
                  {mode.icon} {mode.label}
                </button>
              ))}
            </div>
            <p className={styles.modeHint}>{MODE_HINT[authMode]}</p>

            {error && (
              <div
                ref={errorRef}
                className={styles.errorAlert}
                role="alert"
                tabIndex={-1}
                id="login-page-error"
              >
                <ExclamationCircleOutlined className={styles.errorIcon} aria-hidden />
                <div className={styles.errorContent}>
                  <div className={styles.errorTitle}>Could not sign in</div>
                  <div className={styles.errorText}>{error}</div>
                </div>
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => setError(null)}
                  className={styles.dismissBtn}
                  aria-label="Dismiss error"
                />
              </div>
            )}

            <Form
              form={form}
              name="login"
              onFinish={onFinish}
              layout="vertical"
              requiredMark={false}
              className={styles.formFields}
              disabled={isLoading}
              aria-busy={isLoading}
            >
              <Form.Item
                name="username"
                htmlFor="login-username"
                label={<span className={styles.fieldLabel}>Username</span>}
                rules={[
                  { required: true, message: 'Enter your username' },
                  { min: 3, message: 'Username must be at least 3 characters' },
                ]}
                validateTrigger={['onBlur', 'onSubmit']}
              >
                <Input
                  prefix={<UserOutlined aria-hidden />}
                  id="login-username"
                  name="username"
                  placeholder="Enter your username"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  aria-required="true"
                  aria-describedby={error ? 'login-page-error' : undefined}
                />
              </Form.Item>

              {authMode === 'password' && (
                <>
                  <div className={styles.fieldRow}>
                    <label className={styles.fieldLabel} htmlFor="login-password">
                      Password
                    </label>
                    <a href="/auth/forgot-password" className={styles.forgotLink}>
                      Forgot password?
                    </a>
                  </div>
                  <Form.Item
                    name="password"
                    htmlFor="login-password"
                    rules={[
                      { required: true, message: 'Enter your password' },
                      { min: 8, message: 'Password must be at least 8 characters' },
                    ]}
                    validateTrigger={['onBlur', 'onSubmit']}
                  >
                    <Input.Password
                      prefix={<LockOutlined aria-hidden />}
                      id="login-password"
                      name="password"
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      aria-required="true"
                    />
                  </Form.Item>
                </>
              )}

              {authMode === 'mfa' && (
                <Form.Item
                  name="mfa_token"
                  htmlFor="login-mfa"
                  label={<span className={styles.fieldLabel}>Authenticator code</span>}
                  rules={[
                    { required: true, message: 'Enter your MFA code' },
                    { len: 6, message: 'MFA code must be 6 digits' },
                    { pattern: /^\d{6}$/, message: 'MFA code must be 6 digits' },
                  ]}
                  validateTrigger={['onBlur', 'onSubmit']}
                >
                  <Input
                    prefix={<SafetyOutlined aria-hidden />}
                    id="login-mfa"
                    name="mfa_token"
                    placeholder="000000"
                    maxLength={6}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    className={styles.mfaInput}
                    aria-required="true"
                  />
                </Form.Item>
              )}

              {authMode === 'passkey' && (
                <div className={styles.passkeyPanel}>
                  <SafetyOutlined className={styles.passkeyIcon} aria-hidden />
                  <div className={styles.passkeyTitle}>Passkey authentication</div>
                  <div className={styles.passkeyText}>
                    Use your biometric authentication or security key after submitting your username
                  </div>
                </div>
              )}

              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                block
                icon={!isLoading ? <ArrowRightOutlined /> : undefined}
                iconPlacement="end"
                className={styles.submitBtn}
              >
                {isLoading
                  ? 'Signing in…'
                  : authMode === 'password'
                    ? 'Sign in'
                    : authMode === 'mfa'
                      ? 'Sign in with MFA'
                      : 'Authenticate with passkey'}
              </Button>
            </Form>

            <div className={styles.securityBadge}>
              <SafetyOutlined className={styles.securityBadgeIcon} aria-hidden />
              Enterprise-grade security and encryption
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
